import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Adds the selected products as separate lines using the theme's standard cart event contract.
 * @extends {Component<{form: HTMLFormElement, submit: HTMLButtonElement, total: HTMLElement, status: HTMLElement}>}
 */
class DSProductBundle extends Component {
  requiredRefs = ['form', 'submit', 'total', 'status'];
  /** @type {AbortController | undefined} */
  #request;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#enableVariants();
    this.updateBundle();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enableVariants();
    if (!this.#busy) this.updateBundle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    this.#busy = false;
  }

  #enableVariants() {
    for (const selector of this.querySelectorAll('select[data-bundle-variant]')) {
      if (selector instanceof HTMLSelectElement) selector.disabled = this.#busy;
    }
    for (const fallback of this.querySelectorAll('[data-bundle-fallback]')) {
      if (fallback instanceof HTMLInputElement) fallback.disabled = true;
    }
  }

  /** Updates the displayed variant prices, minimum quantities, and total. @param {Event} [_event] */
  updateBundle(_event) {
    let total = 0;
    let purchasable = true;
    const rows = this.querySelectorAll('[data-bundle-row]');
    if (!rows.length) purchasable = false;
    for (const row of rows) {
      const control = row.querySelector('[data-bundle-variant]');
      const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
      const quantityInput = row.querySelector('[data-bundle-quantity]');
      if (!(option instanceof HTMLElement) || !(quantityInput instanceof HTMLInputElement)) {
        purchasable = false;
        continue;
      }
      const quantity = Number(option.dataset.quantity) || 1;
      const price = Number(option.dataset.price) * quantity;
      quantityInput.value = String(quantity);
      total += price;
      if (option.dataset.available !== 'true') purchasable = false;
      const priceLabel = row.querySelector('[data-bundle-price]');
      if (priceLabel) priceLabel.innerHTML = this.#money(price);
      const quantityLabel = row.querySelector('[data-quantity-label]');
      const quantityValue = row.querySelector('[data-quantity-value]');
      if (quantityLabel instanceof HTMLElement) quantityLabel.hidden = quantity === 1;
      if (quantityValue) quantityValue.textContent = String(quantity);
    }
    this.refs.total.innerHTML = this.#money(total);
    this.refs.submit.disabled = !purchasable || this.#busy;
  }

  /** @param {number} amount */
  #money(amount) {
    return formatMoney(amount, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'AED');
  }

  /** @param {SubmitEvent} event */
  async addBundle(event) {
    event.preventDefault();
    if (this.#busy || this.refs.submit.disabled || !this.refs.form.reportValidity()) return;
    const data = new FormData(this.refs.form);
    /** @type {Map<string, number>} */
    const quantities = new Map();
    for (const [key, value] of data) {
      const match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (!match || typeof value !== 'string' || !/^\d+$/.test(value)) continue;
      const quantity = Number(data.get(`items[${match[1]}][quantity]`));
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;
      quantities.set(value, (quantities.get(value) || 0) + quantity);
    }
    if (!quantities.size) return;
    const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
    this.#busy = true;
    this.#enableVariants();
    this.refs.submit.disabled = true;
    this.refs.form.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = '';
    const controller = new AbortController();
    this.#request = controller;
    const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'), (element) =>
      element instanceof HTMLElement ? element.dataset.sectionId : undefined
    ).filter(Boolean))].slice(0, 5);
    try {
      const addUrl = new URL(this.refs.form.action);
      if (!addUrl.pathname.endsWith('.js')) addUrl.pathname += '.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        await this.#refreshCart(controller.signal, undefined, false);
        throw new Error(this.dataset.error);
      }
      await this.#refreshCart(controller.signal, result.sections, true, items);
      if (this.isConnected) this.refs.status.textContent = this.dataset.success || '';
    } catch (error) {
      if (controller.signal.aborted) return;
      if (this.isConnected) this.refs.status.textContent = this.dataset.error || '';
      this.dispatchEvent(new CartErrorEvent({ error: this.dataset.error || '', code: 'INVALID' }));
    } finally {
      if (this.#request === controller) {
        this.#request = undefined;
        this.#busy = false;
        if (this.isConnected) {
          this.refs.form.removeAttribute('aria-busy');
          this.#enableVariants();
          this.updateBundle();
        }
      }
    }
  }

  /**
   * Reconcile the full cart, including when Shopify rejects some requested stock.
   * @param {AbortSignal} signal
   * @param {Record<string, string> | undefined} sections
   * @param {boolean} added
   * @param {Array<{id: number, quantity: number}>} [items]
   */
  async #refreshCart(signal, sections, added, items = []) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(this.dataset.error);
    const cart = await response.json();
    const deferred = CartLinesUpdateEvent.createPromise();
    // Settle first: every consumer receives a handled, completed cart snapshot.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections, source: 'ds-product-bundle', sourceId: this.id, didError: !added },
    });
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: added ? 'add' : 'update',
      context: 'product',
      lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
      promise: deferred.promise,
    }));
  }
}

if (!customElements.get('ds-product-bundle')) {
  customElements.define('ds-product-bundle', DSProductBundle);
}
